import type { ComponentType } from 'react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { type SolutionData, type SolutionLayout } from '@/data/solutions'
import { customerStories, featuredStories } from '@/data/customers'
import { InsightsLayout } from './layouts/InsightsLayout'
import { CreativeLayout } from './layouts/CreativeLayout'
import { WorksLayout } from './layouts/WorksLayout'
import { StatementLayout } from './layouts/StatementLayout'
import type { SolutionLayoutProps } from './layouts/shared'

interface SolutionPageProps {
  solution: SolutionData
}

/** One composition per page, chosen by the solution's `layout` field. */
const LAYOUTS: Record<SolutionLayout, ComponentType<SolutionLayoutProps>> = {
  insights: InsightsLayout,
  creative: CreativeLayout,
  works: WorksLayout,
  statement: StatementLayout,
}

/**
 * Solutions page template: meta, the landing shell and the solution's own
 * layout (see `SolutionLayout` in data/solutions.ts). Every layout carries the
 * same content: headline, stats, steps, the challenge, features, FAQ with the
 * featured customer story, and the CTA band.
 */
export function SolutionPage({ solution }: SolutionPageProps) {
  const story =
    customerStories.find((s) => s.id === solution.customerId) ?? featuredStories[0] ?? customerStories[0]!
  const Layout = LAYOUTS[solution.layout]

  return (
    <>
      <PageMeta title={solution.metaTitle} description={solution.metaDescription} />

      <LandingLayout>
        <div data-layout={solution.layout} className="contents">
          <Layout solution={solution} story={story} />
        </div>
      </LandingLayout>
    </>
  )
}
